import { Typography } from '@mui/material'
import { PromotedProductsGrid } from './PromotedProductsBlock'
import { PromotedProductDTO } from '@hazadapt-git/public-core-base'
import PromotedProductsCustom from '../../assets/icons/PromotedProductsCustom.svg'
import { makeStyles } from 'tss-react/mui'
import {
    customBorderRadius,
    customSafetyProductsColor,
    successColor,
    primaryIconSize,
    errorColor,
} from '../../lib/styles/universal'
import * as clipboard from 'clipboard-polyfill'
import { useAppSelector } from '../../lib/store'
import { selectLanguage } from '../../lib/slices'
import {
    logEvent,
    toast,
    deleteProductBookmark,
    deleteGuestProductBookmark,
    addProductBookmark,
    addGuestProductBookmark,
    getGuestBookmarkedProducts,
    getPromotedProductsByIds,
} from '../../lib/utils'
import { getHardcodedProducts } from '../../lib/entities'
import { IoBookmark, IoAlertCircle } from 'react-icons/io5'
import { IoIosLink } from 'react-icons/io'
import { useEffect, useState, useCallback } from 'react'

interface RecommendedSafetyProductsBoxProps {
    userId?: number
    onLoadingChange?(loading: boolean): void
}

const FEATURED_PRODUCT_IDS = [497, 21, 276, 428]

const applyGuestBookmarks = (
    products: PromotedProductDTO[],
    userId?: number
): PromotedProductDTO[] => {
    if (userId) return products
    const bookmarkedIds = new Set(
        getGuestBookmarkedProducts().map((product) => product.id)
    )
    return products.map((product) => ({
        ...product,
        bookmarked: bookmarkedIds.has(product.id),
    }))
}

export const RecommendedSafetyProductsBox = ({
    userId,
    onLoadingChange,
}: RecommendedSafetyProductsBoxProps) => {
    const { classes: localClasses } = useLocalStyles()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [promotedPrepProducts, setPromotedPrepProducts] = useState<
        PromotedProductDTO[]
    >([])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        onLoadingChange?.(true)
        setError(false)

        getPromotedProductsByIds(FEATURED_PRODUCT_IDS, !!userId)
            .then((promotedPrepProducts) => {
                if (!cancelled) {
                    setPromotedPrepProducts(
                        applyGuestBookmarks(
                            promotedPrepProducts.length > 0
                                ? promotedPrepProducts
                                : getHardcodedProducts().slice(0, 4),
                            userId
                        )
                    )
                }
            })
            .catch((err) => {
                console.error(err)
                if (!cancelled) {
                    setError(true)
                    setPromotedPrepProducts(
                        applyGuestBookmarks(
                            getHardcodedProducts().slice(0, 4),
                            userId
                        )
                    )
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                    onLoadingChange?.(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [onLoadingChange, userId])

    const language = useAppSelector(selectLanguage)

    const onPromotedProductClick = (product_id: number, url: string) => {
        const product = promotedPrepProducts.find((p) => p.id === product_id)
        logEvent('CLICK_PROMOTED_PRODUCT', {
            via: 'recommended_safety_products_box',
            hazards: product?.hazards,
            product_id,
            product_name: product?.name,
            stage: 'Prepare',
            language,
            retailer_id: product?.retailer.id,
            retailer_name: product?.retailer.name,
            affiliate_link: !!product?.affiliate_link,
        }).catch(console.error)

        window.open(url, '_blank', 'noopener')
    }

    // refer to Hazard.tsx for similar implementation
    const onBookmarkPress = useCallback(
        async (product_id: number) => {
            const product = promotedPrepProducts.find(
                (p) => p.id === product_id
            )
            if (!product) return

            if (product.bookmarked) {
                try {
                    if (userId) {
                        await deleteProductBookmark(product_id)
                    } else {
                        deleteGuestProductBookmark(product_id)
                    }
                    toast(
                        `Removed item from your bookmarked products.`,
                        <IoBookmark
                            color={successColor}
                            size={primaryIconSize}
                        />
                    )

                    setPromotedPrepProducts((prev) =>
                        prev.map((p) =>
                            p.id === product_id
                                ? {
                                      ...p,
                                      bookmarked: false,
                                      num_bookmarks: userId
                                          ? p.num_bookmarks - 1
                                          : p.num_bookmarks,
                                  }
                                : p
                        )
                    )
                } catch (err) {
                    console.error(err)
                    toast(
                        'Unable to remove bookmark. Please try again.',
                        <IoAlertCircle
                            color={errorColor}
                            size={primaryIconSize}
                        />
                    )
                }
            } else {
                logEvent('BOOKMARK_PROMOTED_PRODUCT', {
                    product_id,
                    product_name: product.name,
                    stage: 'Prepare',
                    language,
                    retailer_id: product.retailer.id,
                    retailer_name: product.retailer.name,
                    affiliate_link: !!product.affiliate_link,
                })

                try {
                    if (userId) {
                        await addProductBookmark(product_id)
                    } else {
                        addGuestProductBookmark(product)
                    }
                    toast(
                        `Added item to your bookmarked products.`,
                        <IoBookmark
                            color={successColor}
                            size={primaryIconSize}
                        />
                    )

                    setPromotedPrepProducts((prev) =>
                        prev.map((p) =>
                            p.id === product_id
                                ? {
                                      ...p,
                                      bookmarked: true,
                                      num_bookmarks: userId
                                          ? p.num_bookmarks + 1
                                          : p.num_bookmarks,
                                  }
                                : p
                        )
                    )
                } catch (err) {
                    console.error(err)
                    toast(
                        'Unable to bookmark product. Please try again.',
                        <IoAlertCircle
                            color={errorColor}
                            size={primaryIconSize}
                        />
                    )
                    return
                }
            }
        },
        [promotedPrepProducts, language, userId]
    )

    // refer to Hazard.tsx for similar implementation
    const onProductAreaHover = useCallback(
        async (product_id: number) => {
            const product = promotedPrepProducts.find(
                (p) => p.id === product_id
            )
            logEvent('HOVER_PROMOTED_PRODUCT', {
                product_id,
                product_name: product?.name,
                stage: 'Prepare',
                language,
                via: 'recommended_safety_products_box',
                retailer_id: product?.retailer.id,
                retailer_name: product?.retailer.name,
                affiliate_link: !!product?.affiliate_link,
            }).catch(console.error)
        },
        [promotedPrepProducts, language]
    )

    const onShare = useCallback(
        async (product_id: number, url: string) => {
            const product = promotedPrepProducts.find(
                (p) => p.id === product_id
            )

            logEvent('SHARE_PROMOTED_PRODUCT', {
                product_id,
                product_name: product?.name,
                url,
                via: 'recommended_safety_products_box',
                retailer_id: product?.retailer.id,
                retailer_name: product?.retailer.name,
                stage: 'Prepare',
                language,
                affiliate_link: !!product?.affiliate_link,
            })

            clipboard
                .writeText(url)
                .then(() => {
                    toast(
                        'Link copied!',
                        <IoIosLink
                            color={successColor}
                            size={primaryIconSize}
                        />
                    )
                })
                .catch(console.error)
        },
        [promotedPrepProducts, language]
    )

    return (
        <div className={localClasses.box}>
            <div className={localClasses.border}>
                <div className={localClasses.header}>
                    <img
                        className={localClasses.iconBig}
                        src={PromotedProductsCustom}
                        alt="Shopping cart with a first aid plus in a circle on the cart"
                    />
                    <Typography
                        variant="h1"
                        component="h3"
                        sx={{
                            color: customSafetyProductsColor,
                            fontSize: {
                                xs: '1.25rem',
                                sm: '1.75rem',
                                md: '2.25rem',
                            },
                            textAlign: 'center',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        Recommended Safety Products
                    </Typography>
                </div>

                {error && !loading && (
                    <Typography
                        variant="h4"
                        textAlign="center"
                        color={errorColor}
                    >
                        There was a problem loading products, showing you
                        offline recommendations.
                    </Typography>
                )}

                {promotedPrepProducts.some((p) => p.affiliate_link) ? (
                    <Typography
                        variant="body2"
                        fontStyle="italic"
                        textAlign="center"
                        m="0 auto 2rem"
                        maxWidth="40rem"
                    >
                        Purchases through some of our Recommended Safety Product
                        links support us with a commission at no extra cost to
                        you. Thank you!
                    </Typography>
                ) : null}

                <PromotedProductsGrid
                    products={promotedPrepProducts}
                    onLinkPress={onPromotedProductClick}
                    onBookmarkPress={onBookmarkPress}
                    onProductAreaHover={onProductAreaHover}
                    onShare={onShare}
                    loading={loading}
                    searchQuery=""
                    nonProductItem="/hazards/emergency-items?open_block=5550"
                    disableNonProductLink={error}
                />
            </div>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    button: {
        gap: '1.5rem',
        backgroundColor: customSafetyProductsColor,
        borderRadius: customBorderRadius,
        width: 'auto',
        alignSelf: 'center',
    },
    icon: {
        width: '3rem',
        height: 'auto',
    },
    iconBig: {
        width: '4rem',
        height: 'auto',
        flexShrink: 0,
        '@media (max-width: 600px)': {
            width: '2.5rem',
        },
    },
    box: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        padding: '0 .5rem',
        margin: '2rem 0',
        '@media (max-width: 600px)': {
            margin: '1rem 0',
        },
    },
    border: {
        paddingBottom: '2rem',
        border: '1px #05446A solid',
        borderRadius: customBorderRadius,
        justifyContent: 'center',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        '@media (max-width: 600px)': {
            paddingBottom: '1rem',
        },
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1.5rem',
        padding: '2rem',
        minWidth: 0,
        '@media (max-width: 600px)': {
            gap: '0.5rem',
            padding: '0.75rem',
        },
    },
})
